/**
 * COMMS-12 follow-ups (R-12, R-15, R-17): merging keyset pages into a list on screen. The server
 * orders rows by a microsecond timestamp, then id; the JSON `createdAt` only has milliseconds, so
 * the client never re-sorts — it keeps the server's order and only places whole pages.
 */
type WithId = { id: string };

function without<T extends WithId>(rows: T[], ids: Set<string>): T[] {
  return rows.filter((row) => !ids.has(row.id));
}

const idsOf = (rows: WithId[]) => new Set(rows.map((row) => row.id));

/** Newest-first list (announcements, threads): an older page goes below, duplicates skipped. */
export function appendOlderPage<T extends WithId>(current: T[], older: T[]): T[] {
  return [...current, ...without(older, idsOf(current))];
}

/** Oldest→newest list (messages, replies): an earlier page goes above, duplicates skipped. */
export function prependEarlierPage<T extends WithId>(current: T[], earlier: T[]): T[] {
  return [...without(earlier, idsOf(current)), ...current];
}

/**
 * A refreshed newest page merged into what is shown, keeping older pages the reader loaded.
 * `order` is how the list is displayed. Everything shown that is not on the fresh page is older
 * than it (a newest page is contiguous), so it stays on the far side of the page.
 */
export function mergeNewestPage<T extends WithId>(
  current: T[],
  page: T[],
  order: "newestFirst" | "oldestFirst",
): T[] {
  const older = without(current, idsOf(page));
  return order === "newestFirst" ? [...page, ...older] : [...older, ...page];
}

/**
 * The "load older" cursor after a refresh: while rows older than the fresh page are still shown,
 * the cursor that led past them stays valid; otherwise the fresh page's own cursor applies.
 */
export function cursorAfterRefresh(
  current: WithId[],
  page: WithId[],
  currentCursor: string | null,
  pageCursor: string | null,
): string | null {
  const fresh = idsOf(page);
  return current.some((row) => !fresh.has(row.id)) ? currentCursor : pageCursor;
}

/**
 * True when a refreshed newest page shares no row with what is shown although more rows exist
 * before it — more arrived than one page holds, so merging would leave a silent gap.
 */
export function leavesGap(current: WithId[], page: WithId[], pageCursor: string | null): boolean {
  if (current.length === 0 || !pageCursor) return false;
  const shown = idsOf(current);
  return !page.some((row) => shown.has(row.id));
}

/** A list on screen and the cursor for the page before it. */
export type PagedList<T> = { rows: T[]; cursor: string | null };

/** `list` with a refreshed newest page merged in (see mergeNewestPage / cursorAfterRefresh). */
export function refreshPagedList<T extends WithId>(
  list: PagedList<T>,
  page: PagedList<T>,
  order: "newestFirst" | "oldestFirst",
): PagedList<T> {
  return {
    rows: mergeNewestPage(list.rows, page.rows, order),
    cursor: cursorAfterRefresh(list.rows, page.rows, list.cursor, page.cursor),
  };
}
